import { useCallback, useState } from 'react';
import { DateValue } from 'react-aria-components';

import { getLocalTimeZone } from '@internationalized/date';

import ReservationPeriodDialog from './ReservationPeriodDialog';

import {
  updateAttendanceReservationApproval,
  ApprovalStatus,
} from '@/api/updateAttendanceReservationApproval';
import Button from '@/components/Button/Button';
import { DetailedAttendanceReservation } from '@/types/attendanceReservation';

type Props = {
  selectedDate: DateValue | undefined;
  reservations: DetailedAttendanceReservation[];
  refresh: () => void;
};

const ReservationDetails = ({ selectedDate, reservations, refresh }: Props) => {
  const [isOpenPeriodModal, setIsOpenPeriodModal] = useState(false);
  const [targetReservation, setTargetReservation] = useState<DetailedAttendanceReservation>();
  const formatDate = (date: DateValue) => {
    const d = date.toDate(getLocalTimeZone());
    const weekDay = ['日', '月', '火', '水', '木', '金', '土'][d.getDay()];
    return `${date.year}年${date.month}月${date.day}日(${weekDay})`;
  };

  const handleApproval = useCallback(async (reservationID: number, isApproved: boolean) => {
    const input: ApprovalStatus = {
      approval_status: isApproved ? 'approved' : 'unapproved',
    };
    const { status, result } = await updateAttendanceReservationApproval(reservationID, input);
    if (status === 200) {
      console.log(result);
    }
  }, []);

  return (
    <div className="mt-4 space-y-4">
      {selectedDate && <h2 className="text-lg font-bold">{formatDate(selectedDate)}</h2>}
      {reservations.map((reservation, index) => (
        <div
          key={index}
          className="space-y-2 rounded bg-white p-4"
        >
          <div className="font-bold underline">{reservation.office.name}</div>
          <div className="text-lg font-bold text-blue-1000">{`${reservation.start_time?.substring(0, 5)} ~ ${reservation.end_time?.substring(0, 5)}`}</div>
          <Button
            size="xs"
            variant="outline"
            className="flex-1"
            onClick={() => {
              setTargetReservation(reservation);
              setIsOpenPeriodModal(true);
            }}
          >
            終始時間を変更する
          </Button>
          <hr className="my-4 border-t border-gray-200" />
          <div className="flex items-center space-x-2">
            <span>{reservation.child.last_name + ' ' + reservation.child.first_name}</span>
            <div className="mt-2 flex items-center gap-2">
              <div className="rounded border border-gray-200 bg-orange-100 px-2 py-1 text-sm">
                送迎(来所)
                <span
                  className={`ml-1 rounded px-1 text-xs ${
                    reservation.pickup_info?.isRequired
                      ? 'bg-red-700 text-white'
                      : 'bg-gray-800 text-white'
                  }`}
                >
                  {reservation.pickup_info?.isRequired ? 'あり' : 'なし'}
                </span>
              </div>
              <div className="rounded border border-gray-200 bg-green-100 px-2 py-1 text-sm">
                送迎(退所)
                <span
                  className={`ml-1 rounded px-1 text-xs ${
                    reservation.dropoff_info?.isRequired
                      ? 'bg-red-700 text-white'
                      : 'bg-gray-800 text-white'
                  }`}
                >
                  {reservation.dropoff_info?.isRequired ? 'あり' : 'なし'}
                </span>
              </div>
              <div className="rounded border border-gray-200 bg-blue-100 px-2 py-1 text-sm">
                夕食
                <span
                  className={`ml-1 rounded px-1 text-xs ${
                    reservation.is_dinner ? 'bg-red-700 text-white' : 'bg-gray-800 text-white'
                  }`}
                >
                  {reservation.is_dinner ? 'あり' : 'なし'}
                </span>
              </div>
            </div>
          </div>
          <hr className="my-4 border-t border-gray-200" />
          <div className="flex w-full space-x-8">
            <Button
              size="lg"
              type="submit"
              variant={reservation.approval_status === 'approved' ? 'disabled' : 'solid-fill'}
              onClick={() => handleApproval(reservation.id, true)}
              disabled={reservation.approval_status === 'approved'}
              className="flex-1"
            >
              承認
            </Button>
            <Button
              size="lg"
              type="submit"
              variant={reservation.approval_status === 'unapproved' ? 'disabled' : 'outline'}
              onClick={() => handleApproval(reservation.id, false)}
              disabled={reservation.approval_status === 'unapproved'}
              className="flex-1"
            >
              非承認
            </Button>
          </div>
        </div>
      ))}
      {isOpenPeriodModal && selectedDate && targetReservation && (
        <ReservationPeriodDialog
          isOpen={isOpenPeriodModal}
          setIsOpen={setIsOpenPeriodModal}
          selectedDate={selectedDate}
          reservation={targetReservation}
          afterSubmit={() => refresh()}
        />
      )}
    </div>
  );
};

export default ReservationDetails;
